import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import type { PageResponse } from '@/lib/types';
import type { ReferenceTakeCandidate } from '@/lib/queries/reference-take-moderation';
import { EmptyState } from '@/components/molecules/empty-state';

export type ReferenceTakeQueuePaneProps = {
  page: PageResponse<ReferenceTakeCandidate> | undefined;
  isLoading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
};

const CONTRIBUTOR_FALLBACK = 'Unknown contributor';

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const MS_PER_WEEK = MS_PER_DAY * 7;

const SECONDS_PER_MINUTE = 60;

function formatAge(submittedAt: string): string {
  const elapsed = Date.now() - new Date(submittedAt).getTime();
  if (elapsed < MS_PER_HOUR) return `${Math.max(1, Math.floor(elapsed / MS_PER_MINUTE))}m`;
  if (elapsed < MS_PER_DAY) return `${Math.floor(elapsed / MS_PER_HOUR)}h`;
  if (elapsed < MS_PER_WEEK) return `${Math.floor(elapsed / MS_PER_DAY)}d`;
  return `${Math.floor(elapsed / MS_PER_WEEK)}w`;
}

function formatDuration(durationSeconds: number | null): string | null {
  if (durationSeconds === null) return null;
  const minutes = Math.floor(durationSeconds / SECONDS_PER_MINUTE);
  const seconds = durationSeconds % SECONDS_PER_MINUTE;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function ReferenceTakeQueuePane({
  page, isLoading, currentPage, onPageChange,
}: ReferenceTakeQueuePaneProps): ReactElement {
  if (isLoading || page === undefined) {
    return <div className="p-6 text-[12px] text-[var(--ink-3)]">Loading…</div>;
  }

  if (page.totalElements === 0) {
    return <EmptyState title="Queue is clear" description="No contributions to review." />;
  }

  const hasPagination = page.totalPages > 1;
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage + 1 >= page.totalPages;

  return (
    <div className="flex flex-col h-full border-r border-[var(--rule)]">
      <ul className="flex-1 overflow-auto divide-y divide-[var(--rule)]">
        {page.content.map((candidate) => {
          const duration = formatDuration(candidate.durationSeconds);
          return (
            <li key={candidate.referenceTakeId}>
              <Link
                to="/admin/reference-takes/$id"
                params={{ id: candidate.referenceTakeId }}
                className="block px-4 py-3 hover:bg-[var(--bg-soft)] focus:bg-[var(--bg-soft)] focus:outline-none"
              >
                <div className="text-[13px] font-semibold text-[var(--ink)] truncate">
                  {candidate.contributedByDisplayName ?? CONTRIBUTOR_FALLBACK}
                </div>
                <div className="text-[11px] text-[var(--ink-4)] mt-0.5">
                  {formatAge(candidate.submittedAt)}
                  {duration ? ` · ${duration}` : ''}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
      {hasPagination && (
        <nav
          aria-label="Reference-take queue pages"
          className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[var(--rule)] text-[12px] text-[var(--ink-3)]"
        >
          <button
            type="button"
            disabled={isFirstPage}
            onClick={() => onPageChange(currentPage - 1)}
            className="px-2 py-1 rounded-[var(--r-sm)] hover:bg-[var(--bg-soft)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ‹ Previous
          </button>
          <span>
            Page {currentPage + 1} of {page.totalPages}
          </span>
          <button
            type="button"
            disabled={isLastPage}
            onClick={() => onPageChange(currentPage + 1)}
            className="px-2 py-1 rounded-[var(--r-sm)] hover:bg-[var(--bg-soft)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next ›
          </button>
        </nav>
      )}
    </div>
  );
}
